'use client';

import { createRoot, type Root } from 'react-dom/client';

import { LoadingAnimation } from './component';

export interface ILoadingShowOptions {
    text?: string;
    mask?: boolean;
    /** 自动关闭毫秒数；不传则依赖 hide() 手动关闭 */
    duration?: number;
}

let activeRoot: Root | null = null;
let activeContainer: HTMLDivElement | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;

function destroyActiveLoading() {
    if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
    }
    activeRoot?.unmount();
    activeRoot = null;
    activeContainer?.remove();
    activeContainer = null;
}

/**
 * 命令式展示全屏 Loading，挂载到 document.body。
 *
 * ```ts
 * LoadingAnimation.show({ mask: true, text: 'loading...' });
 * await doSomething();
 * LoadingAnimation.hide();
 * ```
 */
export function show(options?: ILoadingShowOptions | number): void {
    if (typeof document === 'undefined') {
        return;
    }

    const resolvedOptions: ILoadingShowOptions = typeof options === 'number' ? { duration: options } : (options ?? {});

    destroyActiveLoading();

    const container = document.createElement('div');
    document.body.appendChild(container);
    activeContainer = container;

    const root = createRoot(container);
    activeRoot = root;

    root.render(
        <LoadingAnimation
            visible
            fullScreen
            mask={resolvedOptions.mask ?? true}
            text={resolvedOptions.text ?? 'loading...'}
        />
    );

    if (resolvedOptions.duration) {
        hideTimer = setTimeout(destroyActiveLoading, resolvedOptions.duration);
    }
}

/** 关闭命令式 Loading */
export function hide(): void {
    destroyActiveLoading();
}
